import { EffectCallback, useEffect, useState } from 'react';

import { FeatureFlagManager } from './featureFlagManager';

import type { ReactNode } from 'react';

/* eslint-disable-next-line react-hooks/exhaustive-deps */
export const useEffectOnce = (effect: EffectCallback) => useEffect(effect, []);

/**
 * Initializes LaunchDarkly and makes the useFeatureFlag hook available.
 *
 * Example usage:
 * ```
 * <FeatureFlagProvider
 *   flagEnum={FeatureFlag}
 *   apiKey={AppConfig.launchDarklyKey}
 *   propertyId={propertyId}
 * >
 *   <App />
 * </FeatureFlagProvider>
 * ```
 *
 * Note: stores feature flag manager directly on the FeatureFlagManager class
 * (instead of in Context), so it's shareable outside the current React tree.
 *
 * @param {ReactNode} [props.children] Child elements to wrap with the provider
 * @param {Record<string, string>} [props.flagEnum] Enum listing all usable feature flags.
 * @param {string} [props.apiKey] LaunchDarkly API key.
 * @param {string | number} [props.propertyId] ID of the property the flags are grabbed for.
 */
export const FeatureFlagProvider = <T extends string = string>({
  children,
  flagEnum,
  apiKey,
  propertyId,
}: {
  children: ReactNode;
  flagEnum: Record<string, T>;
  apiKey: string;
  propertyId: string | number;
}) => {
  const [haveFeatureFlagsLoaded, setHaveFeatureFlagsLoaded] = useState(false);

  /**
   * Initialize LaunchDarkly. Includes property ID when setting the context.
   */
  useEffectOnce(() => {
    (async function initLaunchDarkly() {
      const featureFlagManager = new FeatureFlagManager(flagEnum);
      try {
        await featureFlagManager.initialize({
          apiKey,
          property: { id: propertyId },
        });
        setHaveFeatureFlagsLoaded(true);
      } catch (err) {
        console.error('[FeatureFlagProvider] LaunchDarkly init error:', err);
      }
    })();
  });

  // If the feature flags are not loaded yet, return an empty fragment.
  if (!haveFeatureFlagsLoaded) return <></>;

  // Render child elements of hte provider only once the flags are loaded.
  return <>{children}</>;
};
